import { memo } from 'react';
import { Button, Icon, SpaceBetween } from '@amzn/awsui-components-core';

import { SortableDragHandleProps } from '../v2-common/SortableList';
import { useGlobalCursor } from '../v2-hooks/useGlobalCursor';
import styles from './PatternCellActions.module.scss';

interface PatternCellActionsProps {
  /** True when this row is currently in edit mode. */
  readonly isEditing: boolean;
  readonly onEditClick: () => void;
  readonly onSaveClick: () => void;
  readonly onCancelClick: () => void;
  readonly onDeleteClick: () => void;
  readonly dragHandleProps: SortableDragHandleProps;
  readonly isDragging: boolean;
}

/**
 * Renders a single "Actions" cell in the Epics or Troubleshooting
 * table. Shows edit/delete/drag controls in read mode, and
 * save/cancel controls in edit mode.
 */
export const PatternCellActions = memo(function PatternCellActions({
  isEditing,
  onEditClick,
  onSaveClick,
  onCancelClick,
  onDeleteClick,
  dragHandleProps,
  isDragging,
}: PatternCellActionsProps) {
  useGlobalCursor('grabbing', isDragging);
  if (isEditing) {
    return (
      <div className={styles.actions}>
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="icon" iconName="check" ariaLabel="Save" onClick={onSaveClick} />
          <Button variant="icon" iconName="close" ariaLabel="Cancel" onClick={onCancelClick} />
        </SpaceBetween>
      </div>
    );
  }

  return (
    <div className={styles.actions}>
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="icon" iconName="edit" ariaLabel="Edit" onClick={onEditClick} />
        <Button variant="icon" iconName="remove" ariaLabel="Delete" onClick={onDeleteClick} />
        <span
          ref={dragHandleProps.setActivatorNodeRef}
          {...dragHandleProps.attributes}
          {...dragHandleProps.listeners}
          className={styles.dragHandle}
          aria-label="Reorder"
        >
          <Icon name="drag-indicator" />
        </span>
      </SpaceBetween>
    </div>
  );
});
